import { useState, useEffect, useCallback } from "react";
import { useLocalization } from "../../context/LocalizationContext";
import { useWebSocket } from "../../context/WebSocketContext";
import { useAppContext } from "../../context/AppContext";
import { useAIAvatarSession } from "../../context/AIAvatarSessionContext";
import { useCustomAvatarVoice } from "../../hooks/useCustomAvatarVoice";
import { formatUserName } from "../../utils/helpers";
import "./VitalsCheck.css";
import Header from "../../containers/Header";
import Button from "../../components/shared/Button";
import { VITAL_ICONS } from "../../constants";
import micIcon from "../../assets/icons/AI/whiteMic.svg";
import BubblingAIAssistant from "../../components/BubblingAIAssistant";
import { usePodData } from "../../hooks/usePodData";
import { checkVitals } from "../../apis/patient";
import { useAuth } from "../../context/AuthContext";

const VitalsCheck = ({ userData }) => {
  const { t, isRTL } = useLocalization();
  const { subscribe } = useWebSocket();
  const {
    measures,
    setMeasures,
    markStepCompleted,
    aiLanguage,
  } = useAppContext();
  const { session } = useAuth();
  const [isMeasuring, setIsMeasuring] = useState(
    !(measures && Array.isArray(measures) && measures.length > 0)
  );




  // Localize vital names using backend measurementType key
  const getLocalizedVitalName = (measurementType) => {
    const translated = t(`measureAspects.${measurementType}`);
    // If translation is found (not returning the key itself), use it
    if (translated !== `measureAspects.${measurementType}`) {
      return translated;
    }
    // Fallback: format the raw key as readable text
    return measurementType
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Subscribe to Vitals event from WebSocket
  useEffect(() => {
    const handleVitalsEvent = (data) => {
      console.log("Vitals event received:", data);

      // Extract measurements from the payload
      const measurements =
        data?.payload?.measurements || data?.measurements || [];
      // Map and render all received measurements
      const mappedMeasures = measurements
        .map((m) => {
          const rawName = (m.measurementType || m.name || "").trim();

          return {
            name: rawName,
            value: m.value?.toString?.() ?? "",
            unit: m.unit || "",
          };
        })
        .filter((m) => m.name);

      // Update measures in store and stop measuring
      setMeasures(mappedMeasures);
      markStepCompleted("vitals");
      setIsMeasuring(false);
    };

    // Subscribe to the Vitals event
    const unsubscribe = subscribe("Vitals", handleVitalsEvent);

    return () => {
      unsubscribe();
    };
  }, [subscribe, setMeasures, markStepCompleted]);

  // Check measures in store on mount
  useEffect(() => {
    console.log("measures from store", measures);
    if (measures && Array.isArray(measures) && measures.length > 0) {
      // Measures exist in store, don't start measuring
      setIsMeasuring(false);
    } else {
      // No measures in store, start loading
      setIsMeasuring(true);
    }
  }, [measures]);
  useEffect(() => {
    setInstructionsEnabled(true);
  }, []);

  const userName = formatUserName(userData?.name ?? "");

  const {
    audioRef,
    videoRef,
    instructionVideoSrc,
    sessionToken,
    sessionId,
    isConnected,
    transcription,
    avatarSpeaking,
    repeat,
    initializeSession,
    isAgentConnecting,
    isVideoReady,
    setIsAgentConnecting,
    agentError,
    setAgentError,
    clearInstructionVideoSrc,
    setInstructionsEnabled
  } = useAIAvatarSession();

  const { isRecording, startRecording, stopRecording } = useCustomAvatarVoice({
    sessionId,
    repeat,
    language: aiLanguage,
  });

  const handleStartSpeaking = useCallback(async () => {
    // First time: initialize session
    if (!sessionToken && !isAgentConnecting) {
      try {
        await initializeSession();
      } catch {
        setAgentError(t("liveAvatar.failedToConnect"));
      }
      return;
    }

    // Toggle recording once session is connected
    if (isConnected) {
      if (isRecording) {
        stopRecording();
      } else {
        await startRecording();
      }
    }
  }, [
    sessionToken,
    isConnected,
    isRecording,
    startRecording,
    stopRecording,
    initializeSession,
    isAgentConnecting,
    setAgentError,
    t,
  ]);

  const handleStartLeeka = () => {
    checkVitals(session?.sessionId).then((data) => {
    }).catch((err) => {
      console.error('Error checking vitals:', err);
    });
  }


  // Automatically initialize shared AI agent session when on measuring phase (same as Dashboard on load)
  useEffect(() => {
    if (!isMeasuring) return;
    if (!sessionToken && !isAgentConnecting) {
      initializeSession().catch((err) => {
        console.error("Avatar init error:", err);
        setAgentError(t("liveAvatar.failedToConnect"));
      });
    }
  }, [isMeasuring, sessionToken, isAgentConnecting, initializeSession, setAgentError, t]);

  useEffect(() => {
    handleStartLeeka();
  }, [])

  return (
    <div className="page-container scrollable-page">
      <Header
        statusText={`${t("loggedInAs")} ${userName}`}
        title={t(isMeasuring ? "vitalsCheck.checkYourVitals" : "vitalsCheck.results")}
        showSettingsButton={true}
        showHomeButton={true}
      />
      {isMeasuring ? (
        <>
          <div className="dashboard-avatar-section">
            <div className="dashboard-avatar-frame">
              {avatarSpeaking && <div className="dashboard-avatar-ring" />}
              <div className="dashboard-avatar-inner">
                {/* {!isAgentConnecting && !agentError && !instructionVideoSrc && (
                  <div className="vitals-avatar-placeholder" />
                )} */}
                {isAgentConnecting && (
                  <div className="dashboard-avatar-loading">
                    <span>{t("dashboard.connectingToAgent")}</span>
                  </div>
                )}
                {agentError && (
                  <div className="dashboard-avatar-error">
                    <span>{agentError}</span>
                  </div>
                )}
                {
                  instructionVideoSrc ? (
                    <video
                      key="instruction-video"
                      src={instructionVideoSrc}
                      className="vitals-instruction-video"
                      playsInline
                      autoPlay
                      muted={false}
                      style={{ display: "block", zIndex: 3000 }}
                      aria-label={t("settings.aiAssistant")}
                      onEnded={() => {
                        clearInstructionVideoSrc();
                        startRecording();
                      }}
                    />
                  ) :
                    (
                      // <audio
                      //   ref={audioRef}
                      //   autoPlay
                      //   muted={false}
                      //   aria-label={t("settings.aiAssistant")}
                      // />
                      <video
                        key="avatar-video"
                        ref={videoRef}
                        className="vitals-instruction-video"
                        playsInline
                        autoPlay
                        muted={false}
                        style={{ display: isVideoReady ? "block" : "none" }}
                        aria-label={t("settings.aiAssistant")}
                      />
                    )
                }
              </div>
            </div>
          </div>

          <p className="dashboard-instruction page-text">
            {transcription || t("vitalsCheck.sessionInstruction", { name: userName })}
          </p>

          <Button
            variant="secondary"
            icon={micIcon}
            iconPosition="start"
            className="bubbling-ai-modal-start-speaking"
            onClick={handleStartSpeaking}
            disabled={isAgentConnecting}
            iconStyle={
              isRTL
                ? { transform: "scaleX(-1)", marginLeft: "1vw", marginRight: 0 }
                : { marginRight: "1vw" }
            }
          >
            {isAgentConnecting
              ? t("dashboard.connectingToAgent")
              : isRecording
                ? t("dashboard.listening")
                : t("dashboard.startSpeaking")}
          </Button>
        </>
      ) : (
        <>
          {/* Results phase (background removed) */}
          <div className="results-container">
            <div className="vitals-grid">
              {measures &&
                Array.isArray(measures) &&
                measures.map((vital, index) => (
                  <div key={index} className="vital-card">
                    <div className="vital-card-header">
                      <div className="vital-name">
                        {getLocalizedVitalName(vital.name)}
                      </div>
                      {VITAL_ICONS[vital.name] && (
                        <div className="vital-icon">
                          <img src={VITAL_ICONS[vital.name]} alt={vital.name} />
                        </div>
                      )}
                    </div>
                    <div className="vital-value-container">
                      <div className="vital-value">{vital.value}</div>
                      <div className="vital-unit">{vital.unit}</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
          <BubblingAIAssistant />
        </>
      )}
    </div>
  );
};

export default VitalsCheck;
